import type { Grade, StyleStats } from '../types'

/**
 * Style DNA — phân tích "chất hình" của một video:
 * độ sáng, tương phản, độ bão hòa, tông ấm-lạnh và nhịp cắt (độ dài cảnh TB).
 * Chạy 100% trong trình duyệt bằng cách lấy mẫu khung hình lên canvas.
 */
export async function analyzeStyle(
  url: string,
  duration: number,
  onStatus?: (s: string) => void,
): Promise<StyleStats> {
  const video = document.createElement('video')
  video.src = url
  video.muted = true
  video.playsInline = true
  await new Promise<void>((res, rej) => {
    video.onloadeddata = () => res()
    video.onerror = () => rej(new Error('Không đọc được video mẫu.'))
  })

  const W = 160
  const H = Math.max(1, Math.round((video.videoHeight / video.videoWidth) * W)) || 90
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!

  const N = 10
  const dur = duration || video.duration || 1
  let sumB = 0
  let sumSat = 0
  let sumWarm = 0
  const lumas: number[] = []
  let cuts = 0
  let prevHist: Float64Array | null = null

  for (let i = 0; i < N; i++) {
    const t = (dur * (i + 0.5)) / N
    onStatus?.(`Phân tích chất hình… ${Math.round(((i + 1) / N) * 100)}%`)
    await seek(video, t)
    ctx.drawImage(video, 0, 0, W, H)
    const { data } = ctx.getImageData(0, 0, W, H)

    let b = 0
    let sat = 0
    let warm = 0
    const hist = new Float64Array(16)
    const px = W * H
    for (let p = 0; p < data.length; p += 4) {
      const r = data[p]
      const g = data[p + 1]
      const bl = data[p + 2]
      const luma = 0.299 * r + 0.587 * g + 0.114 * bl
      b += luma
      const mx = Math.max(r, g, bl)
      const mn = Math.min(r, g, bl)
      sat += mx === 0 ? 0 : (mx - mn) / mx
      warm += (r - bl) / 255
      hist[Math.min(15, (luma / 16) | 0)]++
    }
    b /= px
    sat /= px
    warm /= px
    sumB += b
    sumSat += sat
    sumWarm += warm
    lumas.push(b)

    // phát hiện cắt cảnh: khác biệt histogram luma so với khung trước
    if (prevHist) {
      let diff = 0
      for (let k = 0; k < 16; k++) diff += Math.abs(hist[k] - prevHist[k])
      if (diff / px > 0.5) cuts++
    }
    prevHist = hist
  }

  const brightness = sumB / N / 255
  const saturation = sumSat / N
  const warmth = Math.max(-1, Math.min(1, (sumWarm / N) * 3))
  const meanLuma = sumB / N
  const variance = lumas.reduce((a, l) => a + (l - meanLuma) ** 2, 0) / N
  const contrast = Math.min(1, Math.sqrt(variance) / 128)
  const avgShotLen = cuts > 0 ? dur / (cuts + 1) : dur

  return { brightness, contrast, saturation, warmth, avgShotLen }
}

/** Từ chất hình mẫu và chất hình dự án, tính bộ grade để "kéo" dự án về giống mẫu. */
export function gradeToMatch(project: StyleStats, ref: StyleStats): Grade {
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))
  const ratio = (a: number, b: number) => (b < 0.02 ? 1 : clamp(a / b, 0.5, 1.8))
  return {
    brightness: ratio(ref.brightness, project.brightness),
    contrast: ratio(ref.contrast || 0.3, project.contrast || 0.3),
    saturate: ratio(ref.saturation, project.saturation),
    warmth: clamp(ref.warmth - project.warmth, -1, 1),
  }
}

/** Điểm giống nhau 0..100 giữa hai chất hình. */
export function styleScore(a: StyleStats, b: StyleStats): number {
  const d =
    Math.abs(a.brightness - b.brightness) +
    Math.abs(a.contrast - b.contrast) +
    Math.abs(a.saturation - b.saturation) +
    Math.abs(a.warmth - b.warmth) / 2
  return Math.max(0, Math.round((1 - d / 3) * 100))
}

function seek(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve) => {
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked)
      resolve()
    }
    video.addEventListener('seeked', onSeeked)
    video.currentTime = t
  })
}
